/**
 * Cookie consent for the public site (B3-home, DES-14).
 *
 * Google Tag Manager loads only once the visitor accepted. The choice is
 * kept in localStorage; with no choice (or storage blocked) nothing loads
 * and the banner asks again. Refusing keeps GTM off and expires the
 * analytics cookies it had set (QA-W6-3).
 */
export type ConsentChoice = 'granted' | 'denied';

export const CONSENT_STORAGE_KEY = 'xdial_cookie_consent';

export function readConsent(): ConsentChoice | null {
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export function writeConsent(choice: ConsentChoice | null): void {
  try {
    if (choice) window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
    else window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Storage blocked: the choice holds for this page only.
  }
}

export const GTM_ID = 'GTM-TRSPQ6FS';
const GTM_SCRIPT_ID = 'gtm-xdial';

type DataLayerWindow = Window & { dataLayer?: unknown[] };

/** Injects GTM once. Callers check consent first. */
export function loadGtm(): void {
  const w = window as DataLayerWindow;
  if (document.getElementById(GTM_SCRIPT_ID)) return;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

export function isGtmLoaded(): boolean {
  return document.getElementById(GTM_SCRIPT_ID) !== null;
}

/**
 * Cookies set by the analytics tags GTM loads (Google Analytics, and the
 * Axeptio consent tool the GTM container also loads). Refusing expires them
 * (QA-W6-3): reloading stops GTM but leaves what it already wrote.
 */
const ANALYTICS_COOKIE = /^(_ga|_ga_.+|_gid|_gat.*|axeptio_.+)$/;

/** The domains a tag may have written a cookie for: the host and each parent domain (not the TLD). */
export function cookieDomains(hostname: string): string[] {
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length < 2 || /^\d+$/.test(parts[parts.length - 1] ?? '')) return [];
  const out: string[] = [];
  for (let i = 0; i <= parts.length - 2; i += 1) out.push(parts.slice(i).join('.'));
  return out;
}

/** Expires every known analytics cookie for the current host and its parent domains. Returns the names found. */
export function clearAnalyticsCookies(): string[] {
  if (typeof document === 'undefined') return [];
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim() ?? '')
    .filter((n) => ANALYTICS_COOKIE.test(n));
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/';
  const domains = cookieDomains(window.location?.hostname ?? '');
  for (const name of new Set(names)) {
    document.cookie = `${name}=; ${expired}`;
    for (const d of domains) {
      document.cookie = `${name}=; ${expired}; domain=${d}`;
      document.cookie = `${name}=; ${expired}; domain=.${d}`;
    }
  }
  return names;
}

export function pushPageView(pageName: string): void {
  const w = window as DataLayerWindow;
  if (!isGtmLoaded() || !w.dataLayer) return;
  w.dataLayer.push({ event: 'page_view', page_name: pageName });
}
